import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { Eye, EyeOff, Globe, Check } from 'lucide-react';

const languages = [
  { code: "en", label: "English" },
  { code: "ru", label: "Русский" },
];

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [open, setOpen] = useState(false);
  const current = i18n.resolvedLanguage || i18n.language;

  const navigate = useNavigate();

  const welcomeSlides = [
    {
      title: t('login.welcome.title1'),
      description: t('login.welcome.description1'),
      footer: t('login.welcome.footer'),
    },
    {
      title: t('login.welcome.title2'),
      description: t('login.welcome.description2'),
      footer: t('login.welcome.footer'),
    },
    {
      title: t('login.welcome.title3'),
      description: t('login.welcome.description3'),
      footer: t('login.welcome.footer'),
    },
    {
      title: t('login.welcome.title4'),
      description: t('login.welcome.description3'),
      footer: t('login.welcome.footer'),
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % welcomeSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [welcomeSlides.length, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/users/login', {
        systemId: username,
        password,
      });

      const { token } = response.data;
      localStorage.setItem('token', token);

      const meRes = await api.get('/users/me');
      const companyId = meRes.data?.data?.companyId;
      localStorage.setItem('companyId', companyId);
      localStorage.setItem('user', JSON.stringify(meRes.data?.data || {}));

      if (meRes.data?.data.systemRole == 'COMPANY_EMPLOYEE') {
        navigate('/profile', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        t('login.error.generic');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-5 sm:p-6">
      {/* Main container — centered and responsive width */}
      <div className="w-full max-w-md md:max-w-5xl bg-gray-900/40 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden">

        {/* Desktop: grid with two columns | Mobile: only login (slider hidden) */}
        <div className="grid md:grid-cols-2 min-h-[480px] md:min-h-[520px]">

          {/* ── Slider ── Only visible on md+ screens */}
          <div className="relative hidden md:block bg-gradient-to-br from-gray-950 via-gray-900 to-black/90 p-8 md:p-10 flex flex-col justify-center">

            <div className="relative h-full">
              {welcomeSlides.map((slide, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 flex flex-col justify-center items-center text-center transition-all duration-1000 ease-in-out ${index === currentSlide
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 -translate-y-4'
                    }`}
                >
                  <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent pb-6 md:pb-8">
                    {slide.title}
                  </h1>

                  <p className="text-base md:text-lg text-gray-300 leading-relaxed max-w-md mx-auto mb-8 md:mb-10">
                    {slide.description}
                  </p>

                  <p className="text-sm text-gray-400">
                    {slide.footer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Login Form ── Always visible, takes full width on mobile */}
          <div className="relative p-6 sm:p-8 md:p-10 flex flex-col justify-center">

            {/* Language selector */}
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
              <div className="relative">
                <button
                  onClick={() => setOpen(!open)}
                  className="flex items-center gap-2 bg-gray-800/70 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10 shadow-lg text-white text-sm hover:bg-gray-700/70 transition"
                >
                  <Globe size={18} className="text-gray-300" />
                  {languages.find((l) => l.code === current)?.label || 'Language'}
                </button>

                {open && (
                  <div className="absolute right-0 mt-2 w-40 bg-gray-900/95 backdrop-blur-md border border-white/10 rounded-xl shadow-xl overflow-hidden">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          setOpen(false);
                        }}
                        className="flex items-center justify-between w-full px-4 py-2.5 text-sm text-gray-200 hover:bg-blue-600/20 transition"
                      >
                        {lang.label}
                        {current === lang.code && <Check size={16} className="text-blue-400" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                {t('login.title')}
              </h2>
              <p className="text-gray-400 mt-2 text-sm sm:text-base">
                {t('login.subtitle')}
              </p>
            </div>

            {error && (
              <div className="bg-red-900/40 border border-red-700/50 text-red-200 px-5 py-3 rounded-xl mb-8 text-center text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              <div>
                <input
                  type="text"
                  placeholder={t('login.username')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-5 py-4 bg-gray-800/60 border border-gray-700 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 text-white placeholder-gray-500 transition-all"
                  autoFocus
                  required
                />
              </div>

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-5 py-4 bg-gray-800/60 border border-gray-700 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 text-white placeholder-gray-500 transition-all pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 ${loading
                    ? 'bg-blue-700/50 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg hover:shadow-blue-500/30 hover:scale-[1.015]'
                  }`}
              >
                {loading ? t('login.signingIn') : t('login.signIn')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}